// Composable für alles rund um die geladenen Spieldaten: REST-Fetch mit Retry/Backoff,
// Team-/Liga-Filter, Tag-Navigation. Kapselt den kompletten "Datenzustand" des Widgets,
// getrennt von Darstellung (Komponenten) und Live-Updates (useLiveScores).
import { ref, computed, watch } from 'vue'
import { isoDatum, tagSchluessel, alsArray } from '../lib/format.js'
import { normalizeApiResponse } from '../lib/mapping.js'
import { useLigaFavoriten } from './useLigaFavoriten.js'
import { wendeStoriesAnMatchAn } from '../lib/storyAnMatch.js'

// Geteilter Cache über ALLE Widget-Instanzen hinweg (Spielplan + Startseite, auch mehrere
// gleichzeitig auf derselben Seite) - der Endpoint soll pro identischer Anfrage nur einmal
// aufgerufen werden, nicht einmal pro Widget-Instanz. Schlüssel = die Parameter, die die
// Anfrage tatsächlich beeinflussen; bei abweichenden Parametern (z.B. andere company-id)
// wird trotzdem separat geladen.
const geteilterCache = new Map() // cacheKey -> { matches, loading, error, tabellen, ladeversprechen }

function ermittleCacheKey(props) {
  // Expliziter Override: Falls cacheKey gesetzt ist, wird NUR dieser als Schlüssel genutzt -
  // umgeht die ganze fehleranfällige "alle Props müssen exakt übereinstimmen"-Problematik
  // (unterschiedliche Prop-Defaults, vergessene Deklarationen, Tag-Tippfehler etc.). Einfach
  // auf beiden Widgets denselben cache-key setzen, garantiert denselben Cache-Eintrag - ganz
  // unabhängig davon, ob sonst irgendein anderes Attribut zufällig abweicht.
  if (props.cacheKey) return `explizit:${props.cacheKey}`

  // Werte normalisieren (null/undefined/'' -> einheitlich null) - sonst führen unterschiedliche
  // Prop-Defaults zwischen den Widgets (z.B. Startseite hat gar kein von/bis definiert) zu
  // unterschiedlichen Cache-Keys, obwohl inhaltlich dasselbe gemeint ist ("keine Einschränkung").
  return JSON.stringify({
    companyId: props.companyId,
    apiBase: props.apiBase,
    von: props.von || null,
    bis: props.bis || null,
    scope: props.scope,
    veranstalterId: props.veranstalterId || null,
    priorityLeagues: alsArray(props.priorityLeagues)
  })
}

export function useSpielplanData(props, favoriten) {
  const cacheKey = ermittleCacheKey(props)
  let eintrag = geteilterCache.get(cacheKey)
  if (!eintrag) {
    eintrag = {
      matches: ref([]),
      loading: ref(true),
      error: ref(null),
      tabellen: ref([]), // 'groups' aus der REST-Antwort - Tabellen-Blöcke je Liga/Gruppe
      ladeversprechen: null, // laufender/abgeschlossener Fetch - verhindert Mehrfach-Requests
      geladeneStoryIds: new Map(), // matchId -> { versuche, zuletzt } - geteilt,
      // damit z.B. Spielplan-Widget und Startseite bei überlappenden sichtbaren Spielen nicht
      // beide dieselbe Story doppelt nachladen
      storyRetryTimerGestartet: false // verhindert mehrere parallele Timer, falls mehrere
      // Widget-Instanzen denselben Cache-Eintrag nutzen
    }
    geteilterCache.set(cacheKey, eintrag)
  }
  // matches/loading/error/tabellen/geladeneStoryIds sind ab hier GETEILT über alle Instanzen mit
  // demselben Key - Team-/Liga-Filter und Tag-Navigation bleiben bewusst PRO INSTANZ (jedes
  // Widget hat seine eigene Sicht/Filterung auf dieselben Rohdaten).
  const { matches, loading, error, tabellen, geladeneStoryIds } = eintrag
  const teamFilter = ref('')
  const { ligaFavoriten, istLigaFavorit } = useLigaFavoriten()
  // Startet direkt gefiltert auf die favorisierten Ligen, falls mind. eine gesetzt ist - bei
  // vielen Ligen (z.B. 20) wirkt "Alle" gemischt sonst unübersichtlich und für den Nutzer
  // uninteressant, der ja eigentlich nur seine paar Lieblingsligen sehen will.
  const activeLiga = ref('alle')
  const nurFavoriten = ref(false)
  // GEÄNDERT: Datum-Sprung-Bug bei Filterwechsel (z.B. Liga-Favoriten) behoben - vorher war ein
  // reiner INDEX (aktuellerTagIndex) die Quelle der Wahrheit, das echte Datum wurde nur daraus
  // abgeleitet (verfuegbareTage[index]). Ändert ein Filterwechsel die Zusammensetzung von
  // verfuegbareTage (z.B. weniger Tage bei Favoriten-Liga), zeigte derselbe Index plötzlich auf
  // ein KOMPLETT ANDERES Datum - das war der Sprung. Jetzt ist das DATUM selbst (als
  // tagSchluessel-String, z.B. '2026-09-06') die Quelle der Wahrheit; der Index wird nur noch
  // für die Vor/Zurück-Navigation ABGELEITET, nicht umgekehrt. Bleibt der gewählte Tag nach
  // einem Filterwechsel weiterhin in verfuegbareTage enthalten, bleibt er stabil ausgewählt -
  // kein Sprung mehr. Nur falls er GENAU deswegen rausfällt (z.B. Favoriten-Liga hat an diesem
  // Tag kein Spiel), weicht ausgewaehlterTag auf den nächstgelegenen verfügbaren Tag aus.
  //
  // null (statt vorher -1) ist weiterhin der Sentinel-Wert "noch kein Tag gewählt" - aus
  // demselben Grund wie vorher: verhindert, dass beim ersten Laden kurz ein zufälliger Tag
  // aufblitzt, bevor springeZuHeute() den echten heutigen Tag setzt.
  const ausgewaehlterTagSchluessel = ref(null)

  // Basis-URL für relative Bild-Pfade in Story-Daten (imageUrl/avatarUrl) - Default die
  // Produktions-Domain, lokal beim Testen z.B. per Prop auf "" setzen (dann bleiben Pfade
  // relativ, falls der lokale Dev-Proxy/Server das schon selbst korrekt auflöst). Gleiches
  // Prinzip wie bildBasisUrl in BetmanStoryWidget.vue, nur eben für den Spielplan-/
  // Startseite-Weg hier.
  const bildBasisUrl = props.bildBasisUrl ?? 'https://app.team-match.io'

  const ligen = computed(() => {
    const gesehen = new Map() // ligaKey (eindeutig, fürs Filtern) -> { label, ligaId, flaggeBild }
    for (const m of matches.value) {
      if (!gesehen.has(m.ligaKey)) gesehen.set(m.ligaKey, { label: m.liga, ligaId: m.ligaId, flaggeBild: m.ligaFlaggeBild })
    }

    // Ligen aus der Map holen und sortieren (außer das "Alle"-Element, das bleibt immer vorne)
    const sortierteLigen = [...gesehen.entries()].map(([key, info]) => ({
      id: key,
      label: info.label,
      ligaId: info.ligaId,
      flaggeBild: info.flaggeBild
    })).sort((a, b) => {
      // ➔ Hier nach deiner gewünschten Eigenschaft sortieren (z. B. alphabetisch nach Label)
      return a.label.localeCompare(b.label)
    })

    const eintraege = [
      { id: 'alle', label: 'Alle', ligaId: null, flaggeBild: null }
    ]
    // "Favoriten"-Eintrag nur zeigen, wenn tatsächlich welche gesetzt sind - sonst wäre er ein
    // leerer, verwirrender Filter-Punkt im Dropdown.
    if (ligaFavoriten.value.size > 0) {
      eintraege.push({ id: 'favoriten', label: '⭐ Favoriten', ligaId: null, flaggeBild: null })
    }
    eintraege.push(...sortierteLigen)
    return eintraege
  })

  // Erst nach Team/Liga filtern (unabhängig vom gewählten Tag) ...
  const vorgefilterteMatches = computed(() => {
    return matches.value.filter(m => {
      const ligaPasst = activeLiga.value === 'alle'
        ? true
        : activeLiga.value === 'favoriten'
          ? istLigaFavorit(m.ligaKey)
          : m.ligaKey === activeLiga.value
      return ligaPasst
        && (!teamFilter.value ||
          m.heim.toLowerCase().includes(teamFilter.value.toLowerCase()) ||
          m.auswaerts.toLowerCase().includes(teamFilter.value.toLowerCase()))
        && (!nurFavoriten.value || !favoriten || favoriten.istFavorit(m.heimId) || favoriten.istFavorit(m.auswaertsId))
    })
  })

  // ... daraus die verfügbaren Tage ableiten (chronologisch sortiert) ...
  const verfuegbareTage = computed(() => {
    return [...new Set(vorgefilterteMatches.value.map(m => tagSchluessel(m.zeit)))].sort()
  })

  // Pro Tag: welche Ligen (Kurzform) mit wie vielen Spielen - fürs Kalender-Badge.
  // Respektiert den aktuellen Team-/Liga-Filter (nutzt vorgefilterteMatches, nicht alle matches).
  // ligaId wird mitgeführt, damit der Kalender bei vielen Ligen an einem Tag auf farbige
  // Kategorie-Punkte umschalten kann (siehe lib/ligaTypen.js: ligaKategorie).
  const spieltageGruppen = computed(() => {
    const gruppen = new Map() // tagSchluessel -> Map<ligaKurz, {anzahl, ligaId}>
    for (const m of vorgefilterteMatches.value) {
      const tag = tagSchluessel(m.zeit)
      if (!gruppen.has(tag)) gruppen.set(tag, new Map())
      const proLiga = gruppen.get(tag)
      const bestehend = proLiga.get(m.ligaKurz)
      proLiga.set(m.ligaKurz, { anzahl: (bestehend?.anzahl ?? 0) + 1, ligaId: m.ligaId })
    }
    const ergebnis = {}
    for (const [tag, proLiga] of gruppen) {
      ergebnis[tag] = [...proLiga.entries()]
        .map(([kurz, info]) => ({ kurz, anzahl: info.anzahl, ligaId: info.ligaId }))
        .sort((a, b) => b.anzahl - a.anzahl)
    }
    return ergebnis
  })

  // ausgewaehlterTag: bleibt beim zuletzt gewählten Datum, SOLANGE es weiterhin in
  // verfuegbareTage vorkommt - das ist der eigentliche Fix. Fällt der gewählte Tag durch einen
  // Filterwechsel tatsächlich raus (z.B. an dem Tag hat die Favoriten-Liga kein Spiel), weicht
  // er auf den nächstgelegenen ZUKÜNFTIGEN verfügbaren Tag aus, sonst auf den letzten
  // verfügbaren - vermeidet einen harten Sprung an den Anfang der Liste.
  const ausgewaehlterTag = computed(() => {
    const gewuenscht = ausgewaehlterTagSchluessel.value
    if (gewuenscht == null) return null
    if (verfuegbareTage.value.includes(gewuenscht)) return gewuenscht
    const naechster = verfuegbareTage.value.find(tag => tag >= gewuenscht)
    return naechster ?? verfuegbareTage.value[verfuegbareTage.value.length - 1] ?? null
  })

  // Nur noch für die UI abgeleitet (z.B. "Tag 3 von 12" in einer Anzeige) - NICHT mehr die
  // Quelle der Wahrheit, siehe ausgewaehlterTagSchluessel weiter oben.
  const ausgewaehlterTagIndex = computed(() => verfuegbareTage.value.indexOf(ausgewaehlterTag.value))

  function vorherigerTag() {
    const idx = ausgewaehlterTagIndex.value
    const neuerIdx = Math.max(0, (idx < 0 ? 0 : idx) - 1)
    ausgewaehlterTagSchluessel.value = verfuegbareTage.value[neuerIdx] ?? ausgewaehlterTagSchluessel.value
  }
  function naechsterTag() {
    const idx = ausgewaehlterTagIndex.value
    const neuerIdx = Math.min(verfuegbareTage.value.length - 1, (idx < 0 ? 0 : idx) + 1)
    ausgewaehlterTagSchluessel.value = verfuegbareTage.value[neuerIdx] ?? ausgewaehlterTagSchluessel.value
  }
  function springeZuHeute() {
    const heute = tagSchluessel(new Date())
    let ziel = verfuegbareTage.value.includes(heute) ? heute : null
    if (!ziel) {
      // Kein Spiel exakt heute -> nächsten zukünftigen Spieltag suchen, sonst den letzten vergangenen nehmen
      ziel = verfuegbareTage.value.find(tag => tag >= heute) ?? verfuegbareTage.value[verfuegbareTage.value.length - 1] ?? null
    }
    ausgewaehlterTagSchluessel.value = ziel
  }
  function springeZuTag(schluessel) {
    if (verfuegbareTage.value.includes(schluessel)) {
      ausgewaehlterTagSchluessel.value = schluessel
    }
  }

  // ... und erst danach auf genau den ausgewählten Tag einschränken.
  const filteredMatches = computed(() => {
    return vorgefilterteMatches.value
      .filter(m => tagSchluessel(m.zeit) === ausgewaehlterTag.value)
      .sort((a, b) => new Date(a.zeit) - new Date(b.zeit))
  })

  // Sobald eine Team-Suche läuft, macht die Tages-Einschränkung keinen Sinn mehr - der Nutzer
  // will ja ALLE Treffer sehen, nicht nur die vom gerade ausgewählten Tag. Flache, nach Zeit
  // sortierte Liste über alle Tage/Wettbewerbe hinweg, keine Gruppierung.
  const sucheAktiv = computed(() => teamFilter.value.trim().length > 0)
  const teamTrefferAlleTage = computed(() =>
    [...vorgefilterteMatches.value].sort((a, b) => new Date(a.zeit) - new Date(b.zeit))
  )

  const gruppiertNachLiga = computed(() => {
    const gruppen = {}
    for (const match of filteredMatches.value) {
      if (!gruppen[match.ligaKey]) gruppen[match.ligaKey] = []
      gruppen[match.ligaKey].push(match)
    }
    return Object.entries(gruppen).map(([ligaKey, ms]) => {
      return {
        ligaId: ligaKey,
        label: ligen.value.find(l => l.id === ligaKey)?.label ?? ligaKey,
        matches: ms
      }
    })
  })

  // Gleiche Gruppierung, aber für die Team-Suche über alle Tage - der Liga/Tag-Umschalter soll
  // auch bei aktiver Suche funktionieren, nicht nur die tagesgebundene Ansicht.
  const teamTrefferGruppiertNachLiga = computed(() => {
    const gruppen = {}
    for (const match of teamTrefferAlleTage.value) {
      if (!gruppen[match.ligaKey]) gruppen[match.ligaKey] = []
      gruppen[match.ligaKey].push(match)
    }
    return Object.entries(gruppen).map(([ligaKey, ms]) => {
      return {
        ligaId: ligaKey,
        label: ligen.value.find(l => l.id === ligaKey)?.label ?? ligaKey,
        matches: ms
      }
    })
  })

  // Der eigentliche Netzwerk-Request - läuft NUR aus, wenn noch kein Ladeversprechen für diesen
  // Cache-Key existiert (siehe loadMatches unten). restRetryTimer/-Delay hängen jetzt am geteilten
  // Cache-Eintrag, nicht mehr pro Instanz - es soll ja nur EIN Retry-Zyklus pro Anfrage laufen.
  async function tatsaechlichLaden() {
    loading.value = true
    error.value = null
    try {
      // Datumsbereich: Prop-Wert nutzen, sonst dynamischer Default (90 Tage zurück, 180 Tage voraus)
      const heute = new Date()
      const vonDefault = new Date(heute); vonDefault.setDate(vonDefault.getDate() - 90)
      const bisDefault = new Date(heute); bisDefault.setDate(bisDefault.getDate() + 180)

      // Backend erwartet POST + JSON-Body (kein GET mit Query-Parameter)
      const requestBody = {
        'company-id': props.companyId,
        'from': props.von ?? isoDatum(vonDefault),
        'to': props.bis ?? isoDatum(bisDefault),
        'priority-leagues': alsArray(props.priorityLeagues),
        ...(props.scope === 'veranstalter' ? { veranstalter: props.veranstalterId } : {})
      }

      const res = await fetch(`${props.apiBase}/get-gameplan-from-to`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const raw = await res.json()
      // Flaggen-Pfade aus dem Backend sind relativ (z.B. "/css/flags/4x3/gb-eng.svg") - werden
      // immer von dieser festen Domain ausgeliefert, unabhängig von api-base/ws-url. GEÄNDERT:
      // jetzt über die konfigurierbare bildBasisUrl-Konstante (Prop-gesteuert), statt fest
      // verdrahtet - lokal beim Testen z.B. auf "" setzbar.
      matches.value = normalizeApiResponse(raw['matches-date'] ?? raw, bildBasisUrl)
      tabellen.value = Array.isArray(raw.groups) ? raw.groups : []
      eintrag.restRetryDelay = 5000 // Backoff zurücksetzen nach Erfolg
    } catch (e) {
      error.value = e.message
      clearTimeout(eintrag.restRetryTimer)
      eintrag.restRetryTimer = setTimeout(() => {
        eintrag.restRetryDelay = Math.min((eintrag.restRetryDelay ?? 5000) * 2, 60000)
        eintrag.ladeversprechen = null // Cache zurücksetzen, damit der nächste Aufruf wirklich neu fetcht
        loadMatches()
      }, eintrag.restRetryDelay ?? 5000)
    } finally {
      loading.value = false
    }
  }

  // Von JEDER Instanz beim eigenen onMounted aufgerufen - startet den Fetch nur, wenn noch
  // keiner läuft/gelaufen ist (geteiltes Ladeversprechen). Jede Instanz muss trotzdem ihre
  // eigene Tag-Navigation initialisieren, sobald Daten (egal von wem geladen) verfügbar sind.
  // Gibt das Promise zurück, damit Aufrufer bei Bedarf darauf warten können (z.B. um danach
  // gepufferte WebSocket-Nachrichten nachzuholen).
  function loadMatches() {

    //alert('loadMatches ' + new Date().toLocaleTimeString())

    if (!eintrag.ladeversprechen) {
      eintrag.ladeversprechen = tatsaechlichLaden()
    }
    return eintrag.ladeversprechen.then(() => springeZuHeute())
  }

  // Manueller Retry-Button im Fehler-Zustand - bricht den automatischen Backoff ab und versucht sofort
  function manuellerRetry() {
    clearTimeout(eintrag.restRetryTimer)
    eintrag.restRetryDelay = 5000
    eintrag.ladeversprechen = null
    loadMatches()
  }

  // Stilles Neuladen im Hintergrund (z.B. wenn die App wieder sichtbar wird, oder periodisch) -
  // im Unterschied zu manuellerRetry() KEIN Backoff-Reset nötig (das ist ja kein Fehlerfall) und
  // wirft auch keinen loading-Zustand um, der ein Flackern der Liste verursachen würde - matches
  // wird einfach ausgetauscht, sobald die neuen Daten da sind.
  let refreshLaeuft = false
  async function refreshMatches() {
    if (refreshLaeuft) return // keine überlappenden Refreshes, falls einer noch läuft
    refreshLaeuft = true
    try {
      await tatsaechlichLaden()
    } catch {
      // Fehler beim stillen Refresh einfach ignorieren - der nächste turnusmäßige Versuch
      // (Sichtbarkeitswechsel oder Intervall) holt's dann nach, kein Grund für eine Fehlermeldung
    } finally {
      refreshLaeuft = false
    }
  }

  // ---------- Stories lazy nachladen ----------
  // Nicht mit dem großen Spielplan-Request mitladen (könnte bei 90+180 Tagen Zeitraum tausende
  // Texte sein) - stattdessen nur für die gerade sichtbaren Spiele (aktueller Tag + Filter).
  // geladeneStoryIds kommt vom geteilten Cache-Eintrag (siehe oben).
  //
  // Retry für Auto-Report: Der Spielbericht wird serverseitig erst ~5 Minuten NACH Spielende
  // automatisch generiert - ein einmaliger Check direkt bei Spielende findet also meistens noch
  // nichts. Statt das Match dann für immer als "erledigt" zu markieren, versuchen wir es bei
  // kürzlich beendeten Spielen ohne Story alle 60s erneut, bis zu MAX_VERSUCHE mal (~10 Minuten
  // Gesamtfenster - deckt die 5 Minuten großzügig ab, auch bei leichten Verzögerungen).
  const RETRY_WARTEZEIT_MS = 60_000
  const RETRY_MAX_VERSUCHE = 10

  function brauchtNeuenVersuch(match) {
    const bisheriger = geladeneStoryIds.get(match.id)
    if (!bisheriger) return true // noch nie versucht

    // GEÄNDERT: Retry lief bisher NUR für bereits beendete Spiele (Auto-Report-Fall) - ein noch
    // nicht beendetes Match, das einmal ohne Vorschau "versucht" wurde, wurde NIE WIEDER
    // nachgefragt, selbst wenn die Vorschau später doch noch generiert wurde. Jetzt symmetrisch:
    // Vorschau kann genauso verspätet fertig werden wie der Bericht, deshalb dieselbe
    // Retry-Logik für beide Fälle, nur mit jeweils passendem "ist schon da"-Check.
    const zielFeldSchonDa = match.status === 'beendet' ? match.storyReport : match.storyPreview
    if (zielFeldSchonDa) return false // Ziel-Story schon da, nichts mehr zu tun

    if (bisheriger.versuche >= RETRY_MAX_VERSUCHE) return false // Fenster ausgeschöpft, aufgeben
    return (new Date().getTime() - bisheriger.zuletzt) >= RETRY_WARTEZEIT_MS
  }

  // GEÄNDERT: match.storyPreview/storyReport bleiben der ROHE Text (nur fürs "hat überhaupt eine Story"-Prüfung in
  // DetailOverlay.verfuegbareTabs). Der eigentliche, strukturiert geparste Inhalt kommt jetzt in
  // match.storyDaten - DetailOverlay zeigt damit über BetmanStoryBody dasselbe reiche Layout
  // (Maskottchen/Quoten/Tipp) wie das eigenständige BetmanStoryWidget, statt eines rohen
  // v-html-Blobs. War vorher versehentlich AUSSERHALB von useSpielplanData platziert (Zugriff
  // auf props/matches/geladeneStoryIds/brauchtNeuenVersuch wäre dort gar nicht möglich gewesen,
  // hätte beim Aufruf zum Absturz geführt) - jetzt korrekt innerhalb der Funktion.
  async function ladeStoriesFuerSichtbare(sichtbareMatches) {
    const fehlende = sichtbareMatches.filter(brauchtNeuenVersuch)
    if (fehlende.length === 0) return
    // sofort markieren (Versuch/Zeitstempel), verhindert doppelte parallele Requests
    fehlende.forEach(m => {
      const bisher = geladeneStoryIds.get(m.id)
      geladeneStoryIds.set(m.id, { versuche: (bisher?.versuche ?? 0) + 1, zuletzt: new Date().getTime() })
    })


    try {
      const ids = fehlende.map(m => m.id).join(',')
      const res = await fetch(`${props.apiBase}/widget/get-stories-matches?matchIds=${encodeURIComponent(ids)}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const liste = await res.json()

      // Sortierung (aktuellste Version gewinnt) und Aufbau der Story-Daten
      // liegen in wendeStoriesAnMatchAn (storyAnMatch.js).
      for (const id of new Set(liste.map(e => e.matchId))) {
        const match = matches.value.find(m => m.id === id)
        if (match) wendeStoriesAnMatchAn(match, liste, bildBasisUrl)
      }
    } catch (e) {
      console.warn('Stories konnten nicht geladen werden:', e)
      // Bei Fehlschlag NICHT zurücksetzen - der Versuchszähler/Zeitstempel bleibt stehen,
      // der normale Retry-Rhythmus (alle 60s) greift beim nächsten Aufruf ohnehin.
    }

  }

  return {
    matches, loading, error, teamFilter, activeLiga, nurFavoriten, tabellen,
    ligen, filteredMatches, gruppiertNachLiga, sucheAktiv, teamTrefferAlleTage, teamTrefferGruppiertNachLiga,
    verfuegbareTage, ausgewaehlterTag, ausgewaehlterTagIndex, ausgewaehlterTagSchluessel, spieltageGruppen,
    vorherigerTag, naechsterTag, springeZuHeute, springeZuTag,
    loadMatches, manuellerRetry, refreshMatches,
    ladeStoriesFuerSichtbare // von Widgets mit eigener Tag-/Filter-Logik (z.B. Startseite) direkt
    // mit ihrer eigenen sichtbaren Liste aufzurufen, statt sich auf filteredMatches zu verlassen
  }
}